import { useAppDispatch, useAppSelector } from '../../../../src/store';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { setDuration } from '../../survey/store/survey-filters.slice';
import dayjs from 'dayjs';

type DurationFilter = {
	start: any;
	end: any;
};

type FormValues = {
	duration: [Date | null, Date | null];
};

export const useDurationLocalSurveyFilter = () => {
	const dispatch = useAppDispatch();

	const reduxDuration = useAppSelector((state) => state.surveyFilters.duration);

	const { control, watch, setValue } = useForm({
		defaultValues: {
			duration: [
				reduxDuration?.start ? new Date(reduxDuration.start) : null,
				reduxDuration?.end ? new Date(reduxDuration.end) : null,
			],
		},
	});

	const name = 'duration' as const;
	const currentDuration = watch(name);

	const [transformDuration, setTransformDuration] = useState({
		start: reduxDuration?.start ?? null,
		end: reduxDuration?.end ?? null,
	});

	const prevStartRef = useRef<number | null>(null);
	const prevEndRef = useRef<number | null>(null);

	const debouncedSetTransformDuration = useCallback(
		debounce((duration: [Date | null, Date | null]) => {
			setTransformDuration({
				start: duration[0] ? dayjs(duration[0]).format('YYYY-MM-DD') : null,
				end: duration[1] ? dayjs(duration[1]).format('YYYY-MM-DD') : null,
			});
		}, 300),
		[]
	);

	const debouncedUpdateRedux = useCallback(
		debounce((duration: [Date | null, Date | null]) => {
			dispatch(
				setDuration({
					start: duration[0] ? duration[0].toISOString() : null,
					end: duration[1] ? duration[1].toISOString() : null,
				})
			);
		}, 300),
		[dispatch]
	);

	useEffect(() => {
		const start = currentDuration?.[0]?.getTime() ?? null;
		const end = currentDuration?.[1]?.getTime() ?? null;

		if (start !== prevStartRef.current || end !== prevEndRef.current) {
			prevStartRef.current = start;
			prevEndRef.current = end;

			const payload: [Date | null, Date | null] = [currentDuration?.[0] ?? null, currentDuration?.[1] ?? null];

			debouncedSetTransformDuration(payload);
			debouncedUpdateRedux(payload);
		}
	}, [currentDuration, debouncedSetTransformDuration, debouncedUpdateRedux]);

	useEffect(() => {
		setValue(name, [
			reduxDuration?.start ? new Date(reduxDuration.start) : null,
			reduxDuration?.end ? new Date(reduxDuration.end) : null,
		]);
	}, [reduxDuration, setValue, name]);

	return {
		name,
		control,
		currentDuration,
		transformDuration,
	};
};
